import type { NavBarNode } from './types';

export type OverflowMarker = { id: string; isOverflow: true };
export type RootRowEntry = NavBarNode | OverflowMarker;

/**
 * Build the visible root row: visible-left items + overflow trigger (if needed)
 * + right items. Used both for rendering and arrow-key navigation.
 */
export function buildRootRow(
  left: readonly NavBarNode[],
  right: readonly NavBarNode[],
  maxVisible: number,
  overflowMarker: OverflowMarker
): RootRowEntry[] {
  const visibleLeft = left.slice(0, maxVisible);
  const overflowed = left.length > maxVisible;
  return [...visibleLeft, ...(overflowed ? [overflowMarker] : []), ...right];
}

/**
 * Move from index `current` by `dir` (±1) within `items`. With `wrap = false`,
 * stepping past either edge returns null (no-op). With `wrap = true`, wraps
 * cyclically.
 */
export function siblingAt<T>(
  items: readonly T[],
  current: number,
  dir: 1 | -1,
  wrap: boolean
): T | null {
  if (items.length === 0) return null;
  const next = current + dir;
  if (next < 0 || next >= items.length) {
    if (!wrap) return null;
    return items[(next + items.length) % items.length];
  }
  return items[next];
}

// Assumes an acyclic tree (guaranteed by NavBarItem registration order).
/** Walk parent links from `node` up to the root, returning the chain in root→node order. */
export function ancestorOpenPath(node: NavBarNode): NavBarNode[] {
  const chain: NavBarNode[] = [];
  let cur: NavBarNode | null = node;
  while (cur) {
    chain.unshift(cur);
    cur = cur.parent;
  }
  return chain;
}
